<script setup lang="ts">
import { computed, provide } from 'vue';

export interface RadioGroupProps {
  name: string;
  modelValue?: string | number | boolean;
  errorMessage?: string;
  hasError?: boolean;
  wrapperClass?: string;
  label?: string;
  labelId?: string;
}

const props = withDefaults(defineProps<RadioGroupProps>(), {
  modelValue: undefined,
  errorMessage: undefined,
  hasError: false,
  wrapperClass: undefined,
  label: undefined,
  labelId: undefined,
});

const emit = defineEmits<{
  'update:modelValue': [value: string | number | boolean];
}>();

const showError = computed(() => {
  return props.errorMessage && (props.hasError || !props.modelValue);
});

const wrapperClasses = computed(() => {
  return props.wrapperClass || '';
});

const errorId = computed(() => {
  return props.errorMessage ? `${props.name}-error` : undefined;
});

// Provide the group context to child radio buttons
provide('radioGroup', {
  name: props.name,
  modelValue: computed(() => props.modelValue),
  hasError: computed(() => props.hasError && showError.value),
  errorId: computed(() => errorId.value),
  updateValue: (value: string | number | boolean) => {
    emit('update:modelValue', value);
  },
});
</script>

<template>
  <div :class="wrapperClasses">
    <h3 v-if="label" :id="labelId" class="sr-only">{{ label }}</h3>
    <div v-if="showError" class="error-wrapper">
      <span
        :id="errorId"
        class="bg-error p-2.5 pr-12 mb-2 inline-flex gap-2.5 items-center text-white text-sm"
        role="alert"
        aria-live="polite"
      >
        <i class="w-6 h-5 bg-white icon icon-warning shrink-0"></i>
        {{ errorMessage }}
      </span>
      <div
        role="radiogroup"
        :aria-labelledby="labelId"
        :aria-describedby="errorId"
        :aria-invalid="hasError"
      >
        <slot></slot>
      </div>
    </div>
    <div v-else role="radiogroup" :aria-labelledby="labelId" :aria-invalid="hasError">
      <slot></slot>
    </div>
  </div>
</template>
